// frontend/src/pages/SkillsDashboard.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import authAxios from '../auth/authAxios';
import { useMsal } from "@azure/msal-react";

interface AssessmentHistory {
  attempt_id: string;
  assessment_id: string;
  started_at: string;
  completed_at: string;
  assessment: {
    title: string;
    category: string;
  };
}

const SkillsDashboard: React.FC = () => {
  const [assessmentHistory, setAssessmentHistory] = useState<AssessmentHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

   const { accounts } = useMsal();
    const user = accounts[0];
    const [hasRoleAssessment, setHasRoleAssessment] = useState(false);

    useEffect(() => {
          if (!user) {
            setHasRoleAssessment(false);
            return;
          }

          // Check if user has ChatGPT.Admin role (assuming roles in idToken claims)
          const roles = (user.idTokenClaims?.roles || []) as string[];

          if (roles.includes("ChatGPT.Assessment")) {
            setHasRoleAssessment(true);
          }
          else {
            setHasRoleAssessment(false);
          }
      }, [user]);

  useEffect(() => {
    fetchAssessmentData();
  }, []);

  const fetchAssessmentData = async () => {
    try {
      setLoading(true);

      // Fetch assessment history
      const historyResponse = await authAxios.get('/api/assessments/attempts');
      setAssessmentHistory(historyResponse.data.filter((attempt: any) => attempt.status === 'completed'));

    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load assessment data');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'frontend': return '🎨';
      case 'backend': return '⚙️';
      case 'security': return '🔒';
      case 'devops': return '🚀';
      case 'ai-automation': return '🤖';
      default: return '💻';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (user && !hasRoleAssessment) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">You do not have access to this page.</div>
        </div>
      </div>
    );
  } 

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">My Assessments</h1>
        <p className="text-gray-300">
          View your completed assessments and feedback.
        </p>
      </div>

      {assessmentHistory.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📊</div>
          <h3 className="text-lg font-medium text-white mb-2">No Assessments Yet</h3>
          <p className="text-gray-400 mb-6">
            Complete your first assessment to see your results and feedback.
          </p>
          <Link
            to="/assessments"
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md font-medium transition-colors duration-200"
          >
            Take Your First Assessment
          </Link>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Assessment History */}
          {assessmentHistory.length > 0 && (
            <div className="bg-gray-800 rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-white">Assessment History</h2>
                <Link
                  to="/assessments"
                  className="text-blue-400 hover:text-blue-300 text-sm font-medium"
                >
                  Take More Assessments →
                </Link>
              </div>
              
              <div className="space-y-4">
                {assessmentHistory.slice(0, 5).map((attempt) => (
                  <div key={attempt.attempt_id} className="bg-gray-700 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <span className="text-2xl">{getCategoryIcon(attempt.assessment.category)}</span>
                      <div>
                        <h3 className="text-white font-medium">{attempt.assessment.title}</h3>
                        <p className="text-gray-400 text-sm">
                          Completed on {formatDate(attempt.completed_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <Link
                        to={`/assessments/${attempt.assessment_id}/results/${attempt.attempt_id}`}
                        className="text-blue-400 hover:text-blue-300 text-sm"
                      >
                        View Feedback
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
              
              {assessmentHistory.length > 5 && (
                <div className="text-center mt-4">
                  <button className="text-gray-400 hover:text-gray-300 text-sm">
                    View All {assessmentHistory.length} Assessments
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Call to Action */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-center">
            <h2 className="text-2xl font-bold text-white mb-2">Continue Your Learning Journey</h2>
            <p className="text-blue-100 mb-4">
              Take more assessments to receive personalized feedback and improve your skills.
            </p>
            <Link
              to="/assessments"
              className="bg-white text-blue-600 hover:bg-gray-100 px-6 py-3 rounded-md font-medium transition-colors duration-200"
            >
              Take Another Assessment
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillsDashboard;
